import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Volume2, VolumeX } from "lucide-react";
import { api, fmtPrice } from "../lib/api";
import Seo, { faqSchema } from "../components/Seo";
import HomeTopBanner from "../components/HomeTopBanner";

const IMG = {
    towel: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/q5g0d2db_woman_with_hair_wrapped_towel_glow_skin.webp",
    hands: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/k43bt7rj_woman_applying_cream_hands_on_face_aesthetic.webp",
    curly: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/cfdt9apq_woman_applying_cream_face_beauty-7b6a10c8.webp",
    glow: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/k81uifra_black_woman_face_skincare_upclose_cream.webp",
    japanese: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/tnb1oea9_beautiful_Japanese_woman_with_straight_dark_hair_smiling_sun-640w.webp",
    wetHair: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/egf8evz7_woman_brushing_wet_hair_aesthetic-640w.webp",
    clinicSign: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/1nl8c69o_699deef92552e413fa471ec6-6000x4000-640w.webp",
    clinicLounge: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/9gryboxb_699deef931e22804dd2de92e-6000x4000-640w.webp",
    clinicReception: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/mrw7mh1o_699deef91001a5dd3c368aad-6000x4000-640w.webp",
};

// Cinematic Ken-Burns slideshow uses Zenith's own brand photography.
// Swap to a real .mp4 URL anytime by setting HERO_VIDEO_URL below.
const HERO_VIDEO_URL = ""; // set to e.g. "https://yourcdn.com/zenith-hero.mp4"
const HERO_SLIDES = [IMG.towel, IMG.glow, IMG.japanese, IMG.wetHair, IMG.curly, IMG.hands];

const testimonials = [
    { name: "Jona M.", text: "From start to finish, I felt comfortable and informed. Even after one session, I noticed improvement in my acne scars." },
    { name: "Nasra I.", text: "Kheriya was amazing — she educated me on everything and made me feel completely comfortable. Truly an expert." },
    { name: "Rick H.", text: "Best Botox experience yet. Clean, safe, and great service. I already booked my next IV hydration visit." },
    { name: "Emily T.", text: "Kheriya did an amazing job with microneedling. She's knowledgeable, gentle, and explained everything clearly." },
];

const journey = [
    { n: "01", title: "Consultation", desc: "We listen first. A relaxed conversation about your goals, history, and what feels right for you." },
    { n: "02", title: "Personalized Plan", desc: "A treatment plan curated to your lifestyle, timeline, and budget — never one-size-fits-all." },
    { n: "03", title: "Gentle Treatment", desc: "Clinician-led care in a calm, private setting. Always with comfort measures and clear aftercare." },
    { n: "04", title: "Ongoing Care", desc: "Honest check-ins so results stay subtle, fresh, and uniquely yours." },
];

export default function Home() {
    const [services, setServices] = useState([]);
    const [muted, setMuted] = useState(true);
    const [slide, setSlide] = useState(0);
    const videoRef = React.useRef(null);

    useEffect(() => {
        api.get("/services").then((r) => setServices(r.data || []));
    }, []);

    // Slideshow rotation (when no MP4 provided)
    useEffect(() => {
        if (HERO_VIDEO_URL) return;
        const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5500);
        return () => clearInterval(t);
    }, []);

    const popular = services
        .filter((s) =>
            ["zen-myers-cocktail", "nad-iv-500mg", "zenith-signature-wellness", "botox", "glutathione", "skinny-shot"].includes(s.slug),
        )
        .slice(0, 6);

    return (
        <div data-testid="home-page" className="-mt-0">
            <HomeTopBanner />
            <Seo
                title="Luxe Medspa, IV Therapy & GLP-1 Weight Loss — San Diego"
                description="Zenith Wellness is San Diego's elevated medspa for IV therapy, GLP-1 weight loss, hair restoration, peptides, and aesthetic injectables. Clinician-led, modern, and refined."
                path="/"
                keywords="medspa San Diego, IV therapy San Diego, GLP-1 weight loss, semaglutide San Diego, NAD+ IV, vitamin injections, hair restoration, Zenith Wellness"
                schema={[faqSchema([
                    { q: "Where is Zenith Wellness located?", a: "Zenith Wellness is a luxe medspa in San Diego, California, serving La Jolla, Carmel Valley, Del Mar and Encinitas." },
                    { q: "Do you offer IV therapy and GLP-1 weight loss?", a: "Yes — we offer clinician-led IV vitamin therapy, GLP-1 (semaglutide) weight management, hair restoration, peptides, and aesthetic injectables." },
                    { q: "How do I book a treatment?", a: "Book online through our 3-step flow with optional pay-now or pay-at-appointment, or call 858-905-4117." },
                ])]}
            />
            {/* ============= HERO MOTION ============= */}
            <section className="relative h-screen min-h-[700px] w-full overflow-hidden bg-[#262422]">
                {HERO_VIDEO_URL ? (
                    <video
                        ref={videoRef}
                        autoPlay loop muted={muted} playsInline preload="auto"
                        poster={HERO_SLIDES[0]}
                        className="absolute inset-0 w-full h-full object-cover"
                    >
                        <source src={HERO_VIDEO_URL} type="video/mp4" />
                    </video>
                ) : (
                    <div className="absolute inset-0">
                        {HERO_SLIDES.map((src, i) => (
                            <div
                                key={src}
                                className={`absolute inset-0 transition-opacity duration-[2000ms] ease-out ${
                                    i === slide ? "opacity-100" : "opacity-0"
                                }`}
                                aria-hidden={i !== slide}
                            >
                                <img
                                    src={src}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    style={{
                                        animation: i === slide ? "kenburns 8s ease-out forwards" : "none",
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
                {/* Overlay — gradient ensures text legibility over varied imagery */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-black/70 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/45 to-black/25" />
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent" />
                </div>

                {/* Hero content */}
                <div className="relative h-full max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 flex flex-col">
                    <div className="flex-1 flex items-center pt-28 md:pt-36">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.1, ease: "easeOut" }}
                            className="max-w-3xl"
                        >
                            <div className="eyebrow text-[#A89070] mb-8">
                                — San Diego · Est. 2024
                            </div>
                            <h1 className="serif text-white text-[42px] sm:text-5xl lg:text-[84px] leading-[1.02] font-light">
                                Care that<br />
                                honors your<br />
                                <span className="italic text-[#D9CFB8]">natural beauty.</span>
                            </h1>
                            <p className="mt-8 max-w-xl text-white/85 text-base md:text-lg leading-relaxed font-light">
                                Holistic, modest, subtle aesthetics. IV therapy, weight loss,
                                and aesthetic treatments designed for real, lasting results.
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                <Link to="/services" data-testid="hero-book-btn" className="btn-gold">
                                    Book a Treatment <ArrowRight size={14} />
                                </Link>
                                <Link to="/contact" data-testid="hero-consult-btn" className="btn-ghost-light">
                                    Free Consultation
                                </Link>
                            </div>
                        </motion.div>
                    </div>

                    {/* Bottom bar */}
                    <div className="pb-10 flex flex-col md:flex-row md:items-end justify-between gap-6 text-white">
                        <div className="flex items-center gap-6">
                            <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <Star key={i} size={14} fill="#A89070" className="text-[#A89070]" />
                                ))}
                            </div>
                            <div className="text-sm tracking-wider">
                                5.0 · Loved by 100+ clients
                            </div>
                        </div>
                        {/* Slide dots when slideshow mode */}
                        {!HERO_VIDEO_URL && (
                            <div className="flex items-center gap-2">
                                {HERO_SLIDES.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setSlide(i)}
                                        aria-label={`slide ${i + 1}`}
                                        className={`h-[2px] transition-all ${
                                            i === slide ? "w-10 bg-[#262422]" : "w-5 bg-white/40 hover:bg-white/70"
                                        }`}
                                    />
                                ))}
                            </div>
                        )}
                        {HERO_VIDEO_URL && (
                            <button
                                onClick={() => {
                                    if (!videoRef.current) return;
                                    videoRef.current.muted = !videoRef.current.muted;
                                    setMuted(videoRef.current.muted);
                                }}
                                aria-label="toggle sound"
                                className="w-10 h-10 rounded-full border border-white/40 flex items-center justify-center hover:bg-white/10 transition"
                            >
                                {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {/* ============= MARQUEE ============= */}
            <section className="bg-[#262422] text-[#A89070] py-7 overflow-hidden border-y border-[#A89070]/20">
                <div className="marquee whitespace-nowrap flex gap-16 serif text-2xl md:text-3xl italic">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <React.Fragment key={i}>
                            <span>IV Hydration</span><span>·</span>
                            <span>NAD+ Therapy</span><span>·</span>
                            <span>Botox & Filler</span><span>·</span>
                            <span>GLP-1 Weight Loss</span><span>·</span>
                            <span>Glutathione Glow</span><span>·</span>
                            <span>Concierge Care</span><span>·</span>
                        </React.Fragment>
                    ))}
                </div>
            </section>

            {/* ============= SIGNATURE PACKAGE FEATURE ============= */}
            <section data-testid="signature-package-section" className="bg-[#F2EDE2] py-24 md:py-32 relative overflow-hidden">
                {/* soft gold radial accent */}
                <div className="absolute top-1/2 -translate-y-1/2 right-0 w-[520px] h-[520px] rounded-full bg-[#A89070]/8 blur-3xl pointer-events-none" aria-hidden />

                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 relative">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        {/* Image · left */}
                        <div className="lg:col-span-7">
                            <div className="relative overflow-hidden rounded-sm aspect-[5/4] shadow-[0_40px_80px_-30px_rgba(38,36,34,.35)]">
                                <img
                                    src={IMG.towel}
                                    alt="Zen Revive & Glow — glowing after signature treatment"
                                    className="w-full h-full object-cover hover:scale-[1.03] transition duration-[1200ms]"
                                />
                                {/* Price badge */}
                                <div className="absolute top-6 left-6 bg-[#F2EDE2] px-5 py-3 rounded-sm border border-[#A89070]/30 shadow-lg">
                                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C7E6A] font-medium">Signature</div>
                                    <div className="serif text-3xl mt-1 text-[#262422] leading-none">$389</div>
                                </div>
                                {/* Journey ribbon */}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-6">
                                    <div className="text-[10px] tracking-[0.32em] uppercase text-[#c9b689] font-medium">
                                        01 Revive · 02 Renew · 03 Glow
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Content · right */}
                        <div className="lg:col-span-5">
                            <div className="eyebrow eyebrow-gold" data-testid="signature-eyebrow">
                                ✦ New Signature Experience
                            </div>
                            <h2 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] mt-6">
                                Zen <em className="italic text-[#A89070]">Revive</em><br />
                                & Glow.
                            </h2>
                            <p className="mt-6 text-[#544638] leading-relaxed max-w-md">
                                The ultimate inside-out glow experience. Refresh your skin,
                                replenish your body and radiate confidence in one 90-minute session.
                            </p>

                            {/* What's inside — tight visual list */}
                            <ul className="mt-8 space-y-3.5">
                                {[
                                    { k: "01", label: "Revive: Personalized IV Hydration" },
                                    { k: "02", label: "Renew: Deluxe HydraFacial + Custom Booster" },
                                    { k: "03", label: "Glow: Red Light Therapy" },
                                ].map((i) => (
                                    <li key={i.k} className="flex items-center gap-4 text-[#262422]">
                                        <span className="w-11 h-11 rounded-full border border-[#A89070]/50 bg-white/60 flex items-center justify-center serif italic text-[13px] text-[#A89070] shrink-0">
                                            {i.k}
                                        </span>
                                        <span className="text-[15px] leading-snug">{i.label}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-8 pt-6 border-t border-[#A89070]/25 flex items-baseline gap-3 text-[#8C7E6A] text-[11px] tracking-[0.22em] uppercase">
                                <span>Duration</span>
                                <span className="text-[#262422] serif italic text-base normal-case tracking-normal">
                                    About 90 minutes
                                </span>
                            </div>

                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <Link
                                    to="/book/zen-revive-glow"
                                    data-testid="signature-book-btn"
                                    className="btn-gold inline-flex items-center justify-center gap-3"
                                >
                                    Book This Package <ArrowRight size={14} />
                                </Link>
                                <Link
                                    to="/services/zen-revive-glow"
                                    data-testid="signature-details-btn"
                                    className="inline-flex items-center justify-center gap-2 px-6 py-4 text-[11px] tracking-[0.22em] uppercase text-[#262422] hover:text-[#A89070] transition"
                                >
                                    See what's inside →
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============= NEW: PEPTIDE THERAPY ============= */}
            <section className="bg-[#262422] text-[#F2EDE2] py-20 md:py-28" data-testid="peptide-hero-section">
                <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="eyebrow text-[#A89070] mb-6">✦ New at Zenith</div>
                        <h2 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                            Peptide Therapy in San Diego.
                            <br />
                            <span className="italic text-[#A89070]">Personalized, clinician-led.</span>
                        </h2>
                        <p className="mt-6 text-[#F2EDE2]/80 text-[17px] leading-[1.85] max-w-2xl">
                            Peptides are short chains of amino acids your body uses as signaling molecules — regulating metabolism, recovery, skin renewal and energy. Our four protocols (Comprehensive Wellness, Weight Management, Skin Health, Cellular Energy) are prescribed only after a clinical evaluation.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to="/peptide-therapy-san-diego-ca"
                                data-testid="peptide-learn-btn"
                                className="btn-gold inline-flex items-center justify-center gap-3"
                            >
                                Explore Peptide Therapy <ArrowRight size={14} />
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="peptide-consult-btn"
                                className="inline-flex items-center justify-center gap-2 px-6 py-4 text-[11px] tracking-[0.22em] uppercase text-[#F2EDE2] hover:text-[#A89070] transition"
                            >
                                Book Free Consult →
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="border border-[#A89070]/30 rounded-sm p-8 bg-[#F2EDE2]/[.03]">
                            <div className="eyebrow text-[#A89070] mb-4">The Protocols</div>
                            <ul className="space-y-4 text-[#F2EDE2]/90 text-[15px] leading-relaxed">
                                <li><strong className="text-[#F2EDE2]">Comprehensive Wellness</strong> — steady, long-term vitality & recovery support</li>
                                <li><strong className="text-[#F2EDE2]">Weight Management</strong> — pairs with GLP-1 programs and metabolic support</li>
                                <li><strong className="text-[#F2EDE2]">Skin Health</strong> — inside-out support alongside microneedling or HydraFacial</li>
                                <li><strong className="text-[#F2EDE2]">Cellular Energy</strong> — complements NAD⁺ IV, NAD⁺ IM and Zen Energy Drip</li>
                            </ul>
                            <div className="mt-6 text-[11px] text-[#F2EDE2]/60 leading-relaxed">
                                Individual results vary. Compounded medications are not FDA-approved, and Zenith Wellness prescribes only after a clinical evaluation.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============= INTRO ============= */}
            <section className="max-w-[1300px] mx-auto px-6 md:px-12 lg:px-16 py-28 md:py-40">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-32">
                        <div className="eyebrow eyebrow-gold mb-8">Welcome to Zenith</div>
                        <h2 className="serif text-5xl md:text-6xl lg:text-7xl leading-[1.02]">
                            A simple path<br />
                            to feeling like<br />
                            <span className="italic text-[#A89070]">yourself again.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-7 text-lg leading-[1.85] text-[#544638]">
                        <p>
                            At Zenith Wellness, we believe self-care is not a luxury — it is an
                            essential part of living well. Every service is thoughtfully designed
                            to be medically grounded, approachable, and results-driven, while
                            honoring the powerful connection between inner health and outer beauty.
                        </p>
                        <p>
                            Treatments are curated to support not only your skin and body, but
                            also confidence, vitality, and overall well-being.
                        </p>
                        <hr className="hr-gold my-6" />
                        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3 text-sm tracking-wide">
                            {["Boost energy & hydration", "Improve skin & confidence", "Lose weight safely", "Look refreshed, not overdone"].map((t) => (
                                <div key={t} className="flex items-center gap-3">
                                    <span className="w-1 h-1 rounded-full bg-[#262422]" />
                                    <span>{t}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ============= EDITORIAL IMAGES ============= */}
            <section className="bg-[#E6DEC9]">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-28 md:py-32">
                    <div className="text-center mb-16 md:mb-20">
                        <div className="eyebrow eyebrow-gold mb-6">Our Treatments</div>
                        <h2 className="serif text-5xl md:text-6xl lg:text-7xl leading-[1.02]">
                            Curated for the way<br />
                            <span className="italic text-[#A89070]">you live.</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                        {[
                            { img: IMG.towel, cat: "IV Therapy", title: "Hydration & Energy", desc: "Medical-grade drips that restore from the inside.", link: "/services?cat=IV+Therapy" },
                            { img: IMG.hands, cat: "Aesthetics", title: "Botox & Filler", desc: "Subtle enhancements that honor your natural features.", link: "/services?cat=Neurotoxin" },
                            { img: IMG.japanese, cat: "Wellness", title: "Weight & GLP-1", desc: "Sustainable, clinician-guided weight management.", link: "/contact" },
                            { img: IMG.wetHair, cat: "Glow", title: "IM Shots & Skin", desc: "Quick targeted boosts for energy, glow, and immunity.", link: "/services?cat=IM+Shot" },
                        ].map((t, i) => (
                            <motion.div
                                key={t.title}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.8, delay: (i % 2) * 0.1 }}
                            >
                                <Link
                                    to={t.link}
                                    data-testid={`editorial-card-${i}`}
                                    className="block group"
                                >
                                    <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-[#262422]">
                                        <img
                                            src={t.img}
                                            alt={t.title}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                                        <div className="absolute top-5 left-5 eyebrow text-[#A89070]">
                                            {t.cat}
                                        </div>
                                    </div>
                                    <div className="pt-7 flex items-start justify-between gap-6">
                                        <div>
                                            <h3 className="serif text-3xl md:text-4xl">{t.title}</h3>
                                            <p className="mt-2 text-[#544638] max-w-md">{t.desc}</p>
                                        </div>
                                        <span className="shrink-0 mt-2 w-12 h-12 rounded-full border border-[#A89070]/30 group-hover:bg-[#262422] group-hover:text-[#262422] group-hover:border-[#262422] flex items-center justify-center transition">
                                            <ArrowRight size={16} />
                                        </span>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>

                    <div className="text-center mt-16">
                        <Link to="/services" data-testid="view-all-treatments" className="btn-outline">
                            View All Treatments <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ============= POPULAR ============= */}
            <section className="bg-[#F2EDE2]">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-28 md:py-32">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
                        <div>
                            <div className="eyebrow eyebrow-gold mb-6">Most Loved</div>
                            <h2 className="serif text-5xl md:text-6xl leading-[1.02]">
                                Begin with a<br />
                                <span className="italic text-[#A89070]">client favorite.</span>
                            </h2>
                        </div>
                        <Link to="/services" className="text-sm tracking-[0.25em] uppercase border-b border-[#A89070] pb-1 hover:text-[#262422] transition">
                            See all →
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 lg:gap-x-8">
                        {popular.map((s) => (
                            <Link
                                key={s.slug}
                                to={`/services/${s.slug}`}
                                data-testid={`popular-card-${s.slug}`}
                                className="group block"
                            >
                                <div className="eyebrow text-[#A89070] mb-3">{s.category}</div>
                                <h3 className="serif text-3xl md:text-4xl leading-tight group-hover:text-[#262422] transition">
                                    {s.name}
                                </h3>
                                <p className="mt-3 text-[#544638] line-clamp-2">{s.short_desc}</p>
                                <hr className="my-5 border-[#A89070]/10" />
                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-2xl serif">{fmtPrice(s.price)}</span>
                                    <span className="inline-flex items-center gap-2 bg-[#262422] text-[#F2EDE2] group-hover:bg-[#A89070] group-hover:text-white rounded-full px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] font-semibold transition">
                                        Book <ArrowRight size={13} />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============= JOURNEY ============= */}
            <section className="bg-[#262422] text-[#F2EDE2]">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-28 md:py-32">
                    <div className="text-center mb-16">
                        <div className="eyebrow text-[#A89070] mb-6">Your Experience</div>
                        <h2 className="serif text-5xl md:text-6xl lg:text-7xl leading-[1.02]">
                            A thoughtful path,<br />
                            <span className="italic text-[#A89070]">from start to glow.</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#262422]/15">
                        {journey.map((j, i) => (
                            <motion.div
                                key={j.n}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: i * 0.1 }}
                                className="bg-[#262422] p-10 md:p-12"
                            >
                                <div className="serif text-[#A89070] text-6xl">{j.n}</div>
                                <h3 className="serif text-3xl mt-6">{j.title}</h3>
                                <p className="mt-4 text-[#F2EDE2]/70 leading-relaxed">{j.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============= CLINIC GALLERY ============= */}
            <section className="bg-[#F2EDE2] py-28 md:py-32">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14 items-end">
                        <div className="lg:col-span-7">
                            <div className="eyebrow eyebrow-gold mb-6">Inside Zenith</div>
                            <h2 className="serif text-5xl md:text-6xl lg:text-7xl leading-[1.02]">
                                A calm, private space<br />
                                <span className="italic text-[#A89070]">designed for you.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-5 text-[#544638] text-lg leading-relaxed">
                            Step into a thoughtfully designed clinic in San Diego — natural light,
                            soft palettes, and clinician-led care from the moment you arrive.
                        </div>
                    </div>

                    <div className="grid grid-cols-12 grid-rows-2 gap-3 md:gap-5 h-[520px] md:h-[680px]">
                        <div className="col-span-12 md:col-span-7 row-span-2 overflow-hidden">
                            <img loading="lazy" decoding="async" src={IMG.clinicSign} alt="Zenith Wellness Reception" className="w-full h-full object-cover hover:scale-[1.02] transition duration-700" />
                        </div>
                        <div className="col-span-6 md:col-span-5 overflow-hidden">
                            <img loading="lazy" decoding="async" src={IMG.clinicLounge} alt="Lounge" className="w-full h-full object-cover hover:scale-[1.02] transition duration-700" />
                        </div>
                        <div className="col-span-6 md:col-span-5 overflow-hidden">
                            <img loading="lazy" decoding="async" src={IMG.clinicReception} alt="Reception" className="w-full h-full object-cover hover:scale-[1.02] transition duration-700" />
                        </div>
                    </div>
                </div>
            </section>

            {/* ============= FINANCING STRIP ============= */}
            <section className="bg-[#E6DEC9]">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <div className="eyebrow eyebrow-gold mb-5">Flexible Financing</div>
                            <h2 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                                Pay over time —<br />
                                <span className="italic text-[#A89070]">no surprises.</span>
                            </h2>
                            <p className="mt-6 text-[#544638] text-lg max-w-xl">
                                We've partnered with <strong>Cherry</strong> and <strong>CareCredit</strong> so
                                you can choose the plan that fits your life.
                            </p>
                        </div>
                        <div className="lg:col-span-5 flex flex-col gap-3">
                            <Link to="/financing" data-testid="home-financing-cta" className="btn-primary">
                                Explore Financing Options <ArrowRight size={14} />
                            </Link>
                            <Link to="/shop" data-testid="home-shop-cta" className="btn-outline">
                                Shop Supplements
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============= TESTIMONIALS ============= */}
            <section className="bg-[#F2EDE2] py-28 md:py-40">
                <div className="max-w-[1300px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="text-center mb-16">
                        <div className="eyebrow eyebrow-gold mb-6">Loved by Clients</div>
                        <h2 className="serif text-5xl md:text-6xl">
                            <span className="italic text-[#A89070]">5.0 stars</span> — and counting.
                        </h2>
                        <a
                            href="https://www.google.com/maps/place/Zenith+Wellness/data=!4m2!3m1!1s0x80d95589d34a6d75:0x86e37ae5f83b0b2c"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="google-reviews-read-link"
                            className="inline-flex items-center gap-2 mt-6 text-[13px] tracking-[0.16em] uppercase text-[#A89070] hover:text-[#262422] transition"
                        >
                            <span className="flex gap-0.5">
                                {[1,2,3,4,5].map((s) => (
                                    <Star key={s} size={11} fill="#A89070" className="text-[#A89070]" />
                                ))}
                            </span>
                            Read every review on Google
                        </a>                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
                        {testimonials.map((t, i) => (
                            <motion.figure
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: i * 0.08 }}
                                className="border-l-2 border-[#A89070] pl-7"
                            >
                                <div className="flex gap-1 mb-4">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                        <Star key={s} size={12} fill="#A89070" className="text-[#A89070]" />
                                    ))}
                                </div>
                                <blockquote className="serif text-2xl md:text-3xl leading-[1.3] italic text-[#262422]">
                                    "{t.text}"
                                </blockquote>
                                <figcaption className="mt-5 eyebrow">
                                    — {t.name}
                                </figcaption>
                            </motion.figure>
                        ))}
                    </div>

                    <div className="mt-16 text-center">
                        <a
                            href="https://www.google.com/maps/place/Zenith+Wellness/data=!4m2!3m1!1s0x80d95589d34a6d75:0x86e37ae5f83b0b2c"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="google-reviews-write-link"
                            className="btn-outline inline-flex"
                        >
                            Leave a Google Review <ArrowRight size={14} />
                        </a>                    </div>
                </div>
            </section>

            {/* ============= CTA ============= */}
            <section className="relative bg-[#262422] text-[#F2EDE2] overflow-hidden">
                <div className="absolute inset-0 opacity-30">
                    <img loading="lazy" decoding="async" src={IMG.glow} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-[#262422] via-[#262422]/85 to-[#262422]/40" />
                <div className="relative max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-44 text-center">
                    <div className="eyebrow text-[#A89070] mb-8">Begin Your Journey</div>
                    <h2 className="serif text-6xl md:text-7xl lg:text-8xl leading-[1.02] max-w-4xl mx-auto">
                        Your personalized<br />
                        care plan starts<br />
                        <span className="italic text-[#A89070]">right here.</span>
                    </h2>
                    <p className="mt-8 text-lg text-[#F2EDE2]/80 max-w-xl mx-auto">
                        Free consultations. Honest guidance. No pressure, ever.
                    </p>
                    <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4">
                        <Link to="/contact" data-testid="cta-consult-btn" className="btn-gold">
                            Book Free Consultation <ArrowRight size={14} />
                        </Link>
                        <Link to="/services" data-testid="cta-browse-btn" className="btn-ghost-light">
                            Browse Treatments
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
